import React from 'react';
import { Reservation } from '../../../types/reservation';
import { UserCouponWithCouponAndUsesCount } from '../../../types/coupon';
import { ReservationForm } from '../validators';

/**
 * What happens to the coupon of the saved reservation when it stops using it.
 * @param usesCount how many reservations use the saved coupon
 * @param couponWasPurchased true when this reservation bought the coupon
 */
export const getCouponRefundText = (usesCount: number | undefined, couponWasPurchased: boolean) => {
  if (usesCount === 1) {
    return 'Se cancelará el bono.';
  }
  if (couponWasPurchased) {
    return 'Se devolverá un uso al bono y su compra pasará a otra reserva.';
  }
  return 'Se devolverá un uso al bono.';
};

const getCouponChangeText = (form: ReservationForm, usesCount: number | undefined, couponWasPurchased: boolean) => {
  let refundText = 'Se devolverá un uso al bono anterior.';
  const hasOnlyOneUse = usesCount === 1;
  if (hasOnlyOneUse) {
    refundText = 'Se cancelará el bono anterior.';
  }
  if (couponWasPurchased && !hasOnlyOneUse) {
    refundText = 'Se devolverá un uso al bono anterior y su compra pasará a otra reserva.';
  }
  let newCouponText = 'Se gastará un uso del bono seleccionado.';
  if (form.typeOfCouponToUse === 'share' && form.selectedUserToShareCoupon && form.userCouponId) {
    newCouponText = `El bono seleccionado de ${form.selectedUserToShareCoupon.name} se asignará también a ${form.externalUsers?.[0]?.name} y se gastará un uso.`;
  }
  if (form.typeOfCouponToUse === 'new' && form.newCouponId) {
    newCouponText = 'Se creará un nuevo bono del tipo seleccionado.';
  }
  return (
    <>
      {refundText}
      <br />
      {newCouponText}
    </>
  );
};

interface CouponNoteOptions {
  form: ReservationForm;
  resource: Reservation | undefined;
  isModifying: boolean;
  isEditing: boolean;
  selectedUserCoupon: UserCouponWithCouponAndUsesCount | undefined;
  selectedCouponHasUses: boolean;
  /** How many reservations use the coupon of the saved reservation. */
  usesCount: number | undefined;
  couponWasPurchased: boolean;
}

/** The note under the coupon field that says what saving does to the coupons. */
export const getCouponNoteText = ({
  form,
  resource,
  isModifying,
  isEditing,
  selectedUserCoupon,
  selectedCouponHasUses,
  usesCount,
  couponWasPurchased,
}: CouponNoteOptions) => {
  if (!isModifying) return;
  if (
    isEditing &&
    form?.useCoupon &&
    resource?.userCouponId &&
    form.userCouponId !== resource?.userCouponId &&
    (!!form.userCouponId || !!form.newCouponId)
  ) {
    return getCouponChangeText(form, usesCount, couponWasPurchased);
  }
  if (
    !resource?.userCouponId &&
    form?.useCoupon &&
    form?.userCouponId &&
    selectedUserCoupon?.isActive &&
    selectedCouponHasUses &&
    (!form.typeOfCouponToUse || form.typeOfCouponToUse === 'existing')
  ) {
    return 'Se gastará un uso del bono.';
  }
  if (form.typeOfCouponToUse === 'share' && form.selectedUserToShareCoupon && form.userCouponId) {
    return `El bono de ${form.selectedUserToShareCoupon.name} se asignará también a ${form.externalUsers?.[0]?.name} y se gastará un uso.`;
  }
  if (form.typeOfCouponToUse === 'new' && form.newCouponId) {
    return 'Se creará un nuevo bono.';
  }
  if (isEditing && !form?.useCoupon && resource?.userCouponId) {
    return getCouponRefundText(usesCount, couponWasPurchased);
  }
  return '';
};
